import React from 'react';
import { Handle, Position } from '@xyflow/react';
import { createNode, createEdge, createNodeMenu } from '../RoutingHelpers';
import { createNodesFromOptions } from './OptionNodes';
/**
 * Créer les edges directs depuis l'IVR vers les destinations
 * Pas de nœuds intermédiaires, juste le label de la touche sur l'edge
 */
export const createIVREdges = (routingId, routing, handlers, edgeIdCounter, onEdgeDelete, routingData, onLabelChange = null) => {
  const edges = [];
  if (!routing.options) return { edges, edgeIdCounter };

  Object.entries(routing.options).forEach(([optKey, option]) => {
    let targetNodeId = null;
    let edgeColor = '#6366f1';
    // Déterminer le nœud cible selon le type
    if (option.type === 'queue' && option.queueId) {
      targetNodeId = `queue-${option.queueId}`;
      edgeColor = '#10b981';
    } else if (option.type === 'disconnect') {
      targetNodeId = 'disconnect-node';
      edgeColor = '#ef4444';
    } else if (option.type === 'transfer' && option.targetType === 'phone' && option.targetId) {
      // Format: type: "transfer", targetType: "phone", targetId: "9999"
      targetNodeId = `transfer-pstn-${option.targetId}`;
      edgeColor = '#f97316';
    } else if (option.type === 'transfer' && option.targetType === 'user' && option.targetId) {
      // Format: type: "transfer", targetType: "user", targetId: "userId"
      targetNodeId = `transfer-user-${option.targetId}`;
      edgeColor = '#8b5cf6';
    } else if (option.type === 'transferUser' && option.targetId) {
      // Format: type: "transferUser", targetId: "userId"
      targetNodeId = `transfer-user-${option.targetId}`;
      edgeColor = '#8b5cf6';
    } else if (option.type === 'transfer_pstn' && option.targetPhoneNumber) {
      targetNodeId = `transfer-pstn-${option.targetPhoneNumber}`;
      edgeColor = '#f97316';
    } else if (option.type === 'transfer_user' && option.targetUserId) {
      targetNodeId = `transfer-user-${option.targetUserId}`;
      edgeColor = '#8b5cf6';
    } else if (option.type === 'routing' && option.routingName) {
      // Pour les routings, on détermine le bon ID
      const refRouting = routingData[option.routingName];
      const refRoutingId = option.routingName === '0' ? 'routing-0' : `routing-${option.routingName}`;
      targetNodeId = (refRouting?.type === 'ivr' || refRouting?.type === 'IVR') ? refRoutingId : refRoutingId;
      edgeColor = '#8b5cf6';
    }
    // Créer l'edge direct avec le label de la touche et le handle spécifique
    if (targetNodeId) {
      const edge = createEdge(edgeIdCounter++, routingId, targetNodeId, edgeColor, optKey, onEdgeDelete, onLabelChange);
      edge.sourceHandle = `option-${optKey}`; // Utiliser un handle spécifique pour chaque option
      edges.push(edge);
    }
  });
  return { edges, edgeIdCounter };
};
/**
 * Traiter un routing de type IVR
 */
export const processIVRRouting = (
  routingId,
  routingKey,
  routing,
  routingName,
  routingData,
  xPosition,
  yPosition,
  skipRoutingNode,
  handlers,
  currentEdgeId,
  processedRoutings,
  depth,
  processRouting,
  ROUTING_X_OFFSET,
  existingOptionNodes
) => {
  const nodes = [];
  const edges = [];
  let edgeCounter = currentEdgeId;
  // Nœud IVR principal avec liste des options DTMF et handles dynamiques
  const optionKeys = routing.options ? Object.keys(routing.options) : [];
  const ivrNodeId = skipRoutingNode ? routingId : `${routingId}-ivr`;
  const ivrNode = createNode(
    ivrNodeId,
    xPosition,
    yPosition,
    <div className="node-ivr">
      {createNodeMenu(ivrNodeId, handlers.handleEditNode, handlers.handleDeleteNode)}
      <div className="node-title">IVR - {routingName}</div>
      <div className="node-separator"></div>
      <div className="node-info">
        {routing.displayName && (
          <div className="node-info-item">{routing.displayName}</div>
        )}
        {routing.audio?.type && (
          <div className="node-info-item">{routing.audio.type}</div>
        )}
        {routing.options && Object.keys(routing.options).length > 0 && (
          <>
            <div className="node-info-item">
              {Object.keys(routing.options).length} options
            </div>
            <div className="node-separator"></div>
            {Object.entries(routing.options).map(([key, option]) => {
              let label = 'Option';
              if (option.type === 'queue') {
                label = option.queueDisplayName || 'Queue';
              } else if (option.type === 'disconnect') {
                label = 'Raccrocher';
              } else if (option.type === 'transfer' && option.targetType === 'phone') {
                label = option.targetId || 'Transfert PSTN';
              } else if (option.type === 'transfer' && option.targetType === 'user') {
                label = option.userDisplayName || option.targetDisplayName || option.targetId || 'Utilisateur';
              } else if (option.type === 'transferUser') {
                label = option.userDisplayName || option.targetDisplayName || option.targetId || 'Utilisateur';
              } else if (option.type === 'transfer_pstn') {
                label = option.targetPhoneNumber || 'Transfert';
              } else if (option.type === 'transfer_user') {
                label = option.targetUserDisplayName || 'Utilisateur';
              } else if (option.type === 'routing') {
                label = option.routingName || 'Routing';
              }
              return (
                <div key={key} className="node-info-item" style={{ fontSize: '11px', padding: '2px 0' }}>
                  {key} → {label}
                </div>
              );
            })}
          </>
        )}
      </div>
      {/* Handles dynamiques pour chaque option IVR */}
      {optionKeys.map((key, index) => {
        const totalOptions = optionKeys.length;
        const leftPercentage = totalOptions === 1 ? 50 : (100 / (totalOptions + 1)) * (index + 1);
        return (
          <Handle
            key={key}
            type="source"
            position={Position.Bottom}
            id={`option-${key}`}
            style={{ left: `${leftPercentage}%`, background: '#6366f1' }}
          />
        );
      })}
    </div>,
    '#6366f1'
  );
  nodes.push(ivrNode);
  // Créer les nœuds pour les options (disconnect, queue, transfer, etc.)
  const OPTION_NODES_Y = yPosition + 400; // Position Y statique pour les nœuds d'options
  const optionNodesResult = createNodesFromOptions(
    routing,
    xPosition,
    OPTION_NODES_Y,
    handlers,
    existingOptionNodes
  );
  nodes.push(...optionNodesResult.nodes);
  
  // Calculer combien de nœuds d'options ont été créés pour positionner les routings après
  const optionNodesCount = optionNodesResult.nodes.length;
  
  // Créer les edges directs depuis l'IVR vers les destinations (avec label de touche)
  const ivrEdgesResult = createIVREdges(
    ivrNodeId,
    routing,
    handlers,
    edgeCounter,
    handlers.onEdgeDelete,
    routingData,
    handlers.onEdgeLabelChange // Passer le callback pour l'édition du label DTMF
  );
  edges.push(...ivrEdgesResult.edges);
  edgeCounter = ivrEdgesResult.edgeIdCounter;
  // Traiter les routings référencés (récursion) pour créer leurs nœuds
  if (routing.options) {
    const referencedRoutings = Object.entries(routing.options)
      .filter(([key, opt]) => opt.type === 'routing' && opt.routingName)
      .map(([key, opt]) => ({ key, routingName: opt.routingName }));
    
    if (referencedRoutings.length > 0) {
      const HORIZONTAL_SPACING = 350;
      // Positionner les routings référencés sur une nouvelle ligne, en dessous des nœuds d'options
      const refRoutingsY = OPTION_NODES_Y + 300; // Position Y statique pour les routings référencés (liaison plus longue)
      
      // Centrer les routings référencés sous le nœud parent
      const totalRefWidth = (referencedRoutings.length - 1) * HORIZONTAL_SPACING;
      const startRefX = xPosition - (totalRefWidth / 2);
      
      referencedRoutings.forEach((ref, index) => {
        const refX = startRefX + (index * HORIZONTAL_SPACING);
        
        // Appel récursif pour créer les nœuds du routing référencé
        const refResult = processRouting(
          ref.routingName,
          routingData,
          null, // Pas de source, l'edge a déjà été créé dans createIVREdges
          refX,
          refRoutingsY,
          handlers,
          edgeCounter,
          processedRoutings,
          depth + 1,
          existingOptionNodes
        );
        nodes.push(...refResult.nodes);
        edges.push(...refResult.edges);
        edgeCounter = refResult.edgeIdCounter;
      });
    }
  }
  return { nodes, edges, edgeIdCounter: edgeCounter };
};
